import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Loader2, Copy, Link2, Pencil, Check, X, UserMinus, RefreshCw, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ListSkeleton } from '../components/skeletons';
import RegenerateCodeModal from '../components/RegenerateCodeModal';
import GroupsLoadError from '../components/GroupsLoadError';
import StickyBackPill from '../components/StickyBackPill';
import {
  codeExpiryLabel, copyDeFalloDeGrupo, copyText, isCodeExpired,
  joinedAtLabel, joinLinkFor, conteoDeAlumnos,
} from '../lib/groups';
import useCodeExpiryTick from '../hooks/useCodeExpiryTick';
// PILOTO-2 (firma #83): el banco del grupo. Vive en su propio componente
// porque tiene su propio ciclo de vida (subida, sondeo, reintento, borrado) y
// mezclarlo aqui haria que cualquier retoque del banco pasara por la pantalla
// que gobierna la membresia. El candado es el de la pagina: esta seccion se
// pinta donde ya no puede llegar nadie que no sea el dueno con bandera.
import GroupBankSection from '../components/GroupBankSection';
// PIPE-INST T5 (firma #126): el modal de borrar el grupo. En su propio archivo
// por el mismo criterio que `RegenerateCodeModal` —un modal destructivo se
// prueba solo— y con «Group» en el nombre a proposito, para caer dentro del
// censo de superficies de grupos y de su barrido de imports prohibidos.
import GroupDeleteModal from '../components/GroupDeleteModal';
// PIPE-INST T5.2 (firma #136): el sistema de capas y de escala del carril.
// Se define UNA vez y lo usan las cuatro pantallas del profesor.
// OCR-PROFE-MATERIAL (PR #350, R-2 / #342): esta página ya no importa el vestido
// tocable del carril. Los dos plegables eran sus únicos usuarios aquí y desde esta
// tanda no cambian de color al pasar el puntero ni al tocarlos.
// REMATES-350 (PR #351, R-3 / #347): los dos plegables llevan su propia capa
// (`CAPA_PLEGABLE`, un escalón más clara que el fondo) y la banda subió de tono.
import {
  CAPA_DE_SECCION, CABECERA_DE_SECCION, CHIP_DE_CONTEO, CUERPO_DE_SECCION, ETIQUETA, GUIA_DE_SECCION,
  TITULO_DE_SECCION_PLEGABLE,
} from '../lib/vestidoDelCarril';
// t7-2a (C1, #516): la entrada a "Avance del grupo", arriba de todo.
import AvanceDelGrupoRecuadro from '../components/GroupAvanceRecuadro';

/* PILOTO-1 (firma #79) — LA PANTALLA DE PROFESOR: un grupo por dentro.
 *
 * DÓNDE VIVE: ruta `/groups/:groupId`, dentro de `AppLayout` y detrás de
 * `<InstitutionalRoute>`. Misma doctrina que la lista: guard + 403 del servidor.
 *
 * LA SALIDA SIEMPRE ES ALCANZABLE (regla móvil del repo): el botón «volver» es
 * el PRIMER nodo de la pantalla, mide 44pt y vive dentro del scrollport — nunca
 * puede quedar debajo del notch ni tapado por la barra inferior, porque el
 * chasis ya le reserva los dos huecos. Y el modal de «quitar» trae su propia
 * safe area abajo, porque es `fixed` y no hereda ninguna.
 *
 * PILOTO-1D (firma #82) — Y AHORA SE QUEDA A LA VISTA AL BAJAR. Ser el primer
 * nodo garantizaba que fuera alcanzable AL LLEGAR; en un grupo con lista larga,
 * recorrer a los alumnos lo dejaba fuera de pantalla. Es la misma píldora
 * pegajosa del detalle de Analytics, reusada tal cual desde
 * `components/StickyBackPill.js` — mismo componente, misma factura, un solo
 * sitio que retocar.
 *
 * LO QUE LA LISTA DE ALUMNOS NO HACE: pedir un dato nuevo. Nombre y correo son
 * los que la cuenta ya tenía; el servidor los lee de `users` al momento de
 * responder y el grupo no guarda copia de ninguno.
 */

// post-t7-2c (F1, Anexo D.3): `soloEsqueleto` lo pone `InstitutionalRoute` mientras la sesion de la web resuelve: la
// pagina pinta su esqueleto de carga sin pedir el grupo; al quitarse, esta misma instancia lo pide.
export default function GroupDetailPage({ soloEsqueleto = false } = {}) {
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const { groupId } = useParams();
  const navigate = useNavigate();

  const [group, setGroup] = useState(null);
  const [notFound, setNotFound] = useState(false);
  // PILOTO-1C — el tercer estado, gemelo del de la lista: «no se pudo leer» ya
  // no es un esqueleto que nunca termina. Ver components/GroupsLoadError.js.
  const [errorDeCarga, setErrorDeCarga] = useState(false);
  // T5 -- el modal de borrar el grupo y su bandera de trabajo.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [borrandoGrupo, setBorrandoGrupo] = useState(false);
  // T5.1 (firma #132, §4.1) — la sección de alumnos se pliega.
  // T5.3 (firma #141d) — y nace PLEGADA, siempre: ver el porqué del cambio de
  // criterio junto al desplegador. Sin estado persistido y sin excepción por
  // ruta de llegada, a propósito.
  const [alumnosAbiertos, setAlumnosAbiertos] = useState(false);
  /* TANDA 33b (firma #305) — el bloque del código se pliega, con el MISMO patrón
     y por el mismo motivo. El código de ingreso ocupa media pantalla y es un dato
     que el profesor usa una vez, el día que reparte el grupo; el banco vive
     debajo y es donde pasa el tiempo. `useState(false)` LOCAL y nada más: sin
     `localStorage`, sin parámetro de URL, sin estado global — de modo que entrar
     por donde se entre, el bloque nace cerrado. Lo cobra la prueba C3, que
     desmonta y vuelve a montar. */
  const [codigoAbierto, setCodigoAbierto] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null); // el miembro a quitar
  const [removing, setRemoving] = useState(false);
  // PILOTO-1B: la confirmación de rotar el código, y si ya salió.
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const load = useCallback(async () => {
    setErrorDeCarga(false);
    try {
      const res = await api.get(`/groups/${groupId}`);
      setGroup(res.data.group);
    } catch (err) {
      // 404/403 siguen siendo «este grupo no es tuyo o no existe», que es un
      // hecho sobre el GRUPO y tiene su propia pantalla con salida.
      if (err.response?.status === 404 || err.response?.status === 403) { setNotFound(true); return; }
      // PILOTO-1C — TODO LO DEMÁS YA NO DEJA EL ESQUELETO ETERNO. Antes un 5xx o
      // una red caída disparaban un toast y `group` se quedaba en `null` para
      // siempre: la pantalla cargaba sin fin, sin explicación y sin reintento
      // que no fuera un F5. Declarado en reporte_piloto1.md y cerrado aquí.
      setErrorDeCarga(true);
    }
  }, [groupId]);

  // EL FETCH ES DE MONTAJE (y de cada cambio de `groupId`, que va dentro de
  // `load`). PILOTO-1C sacó a `t` del cierre —el copy del fallo lo pinta ahora
  // `GroupsLoadError`— así que las dependencias se pueden declarar honestas en
  // vez de silenciar la regla. El motivo por el que `t` no podía entrar sigue
  // vigente y por eso salió: no es estable entre renders, y como dependencia
  // re-disparaba la lectura en cada uno, pisando con una respuesta vieja la
  // mutación recién aplicada al estado.
  useEffect(() => { if (!soloEsqueleto) load(); }, [load, soloEsqueleto]);

  // t7-3 remate 7 (pasada 3, regla 7 de #581): la pagina aterriza ARRIBA. El chasis (`<main>` de AppLayout) conserva
  // el desplazamiento de la pantalla anterior: al volver del panel con el ATRAS del navegador quedaba pegada al fondo,
  // con el titulo y el recuadro del avance fuera de la vista (el mismo arreglo que el panel, GroupAvancePage).
  useEffect(() => {
    const chasis = document.querySelector('[data-testid="content-area"]');
    if (chasis) chasis.scrollTop = 0;
  }, []);

  const copy = async (value, okKey) => {
    const ok = await copyText(value);
    if (ok) toast.success(t(okKey));
    else toast.error(t('groups.toasts.copy_failed'));
  };

  const startEdit = () => { setDraftName(group?.name || ''); setEditing(true); };

  const saveName = async (e) => {
    e?.preventDefault();
    const name = draftName.trim();
    if (!name || name === group?.name) { setEditing(false); return; }
    setSaving(true);
    try {
      const res = await api.post(`/groups/${groupId}/rename`, { name });
      setGroup(prev => ({ ...prev, ...res.data.group, members: prev?.members || [] }));
      setEditing(false);
      toast.success(t('groups.toasts.renamed'));
    } catch (err) {
      // PILOTO-1C — el 400 del nombre (mismo tope de 60 que al crear) y el 403
      // del sello de sesión salen en cristiano; el `detail` es la red de abajo.
      toast.error(copyDeFalloDeGrupo(err, t, 'dashboard:groups.toasts.rename_failed',
        { 400: 'name_invalid' }));
    } finally { setSaving(false); }
  };

  // PILOTO-1B — REGENERAR. La respuesta trae el grupo recalculado por el
  // servidor; se funde con el que ya está en pantalla CONSERVANDO la lista de
  // alumnos, que el endpoint de rotación no devuelve (y no tiene por qué: no la
  // toca). Mismo criterio que `saveName`.
  const regenerate = async () => {
    setRegenerating(true);
    try {
      const res = await api.post(`/groups/${groupId}/regenerate_code`);
      setGroup(prev => ({ ...prev, ...res.data.group, members: prev?.members || [] }));
      setConfirmRegen(false);
      toast.success(t('groups.toasts.regenerated'));
    } catch (err) {
      toast.error(copyDeFalloDeGrupo(err, t, 'dashboard:groups.toasts.regenerate_failed'));
    } finally { setRegenerating(false); }
  };

  // -- PIPE-INST T5 (firma #126): BORRAR EL GRUPO Y TODO SU CONTENIDO --------
  //
  // No hay actualizacion optimista y no puede haberla: el grupo deja de existir,
  // asi que lo unico coherente al terminar es SALIR de su pantalla. Se vuelve a
  // la lista, que es de donde el profesor vino.
  const borrarGrupo = async () => {
    setBorrandoGrupo(true);
    try {
      await api.delete(`/groups/${groupId}`);
      setConfirmDelete(false);
      // TANDA 26b (#235) — EL ÉXITO SE ACUSA, Y SE ACUSA ANTES DE NAVEGAR.
      //
      // Hasta aquí el borrado era mudo: el profesor confirmaba la acción más
      // destructiva de su superficie y aparecía en la lista de grupos sin que
      // nadie le dijera que había funcionado. La única prueba era la ausencia de
      // una fila, que es exactamente lo que también se ve cuando algo falla.
      //
      // EL TOAST SOBREVIVE AL `navigate` PORQUE EL `<Toaster>` NO ES DE ESTA
      // PANTALLA: vive en `App.js`, por encima de las rutas, así que el aviso
      // llega a la lista con el profesor. Se emite antes de navegar por orden,
      // no por necesidad de sonner: mientras este componente existe, la llamada
      // está garantizada.
      toast.success(t('groups.toasts.group_deleted'));
      navigate('/groups');
    } catch (err) {
      setConfirmDelete(false);
      // EL COPY ES SUYO Y NO EL DEL ALUMNO (#235). `remove_failed` dice «No se
      // pudo quitar al alumno» y aquí no hay ningún alumno: se estaba borrando
      // el grupo entero. La llave del alumno sigue viva, y sigue siendo del
      // alumno — la usa `removeMember`, doce líneas más abajo.
      toast.error(copyDeFalloDeGrupo(err, t, 'dashboard:groups.toasts.delete_group_failed'));
    } finally { setBorrandoGrupo(false); }
  };

  const removeMember = async () => {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      await api.delete(`/groups/${groupId}/members/${confirmRemove.user_id}`);
      setGroup(prev => ({
        ...prev,
        members: (prev.members || []).filter(m => m.user_id !== confirmRemove.user_id),
        member_count: Math.max(0, (prev.member_count || 1) - 1),
      }));
      setConfirmRemove(null);
      toast.success(t('groups.toasts.member_removed'));
    } catch (err) {
      toast.error(copyDeFalloDeGrupo(err, t, 'dashboard:groups.toasts.remove_failed'));
    } finally { setRemoving(false); }
  };

  // PILOTO-1B: al pasar la hora del vencimiento, esta pantalla se pone en
  // «Vencido» sola (ver hooks/useCodeExpiryTick).
  const transcurrido = useCodeExpiryTick([group?.code_expires_in]);

  // PILOTO-1B — EL VEREDICTO, UNA SOLA VEZ. Lo dictamina el servidor
  // (`code_expired`); `isCodeExpired` solo lo lee y trae el respaldo por fecha
  // para una carga vieja en memoria. Con `group` en null contesta `true`, que da
  // igual: ese bloque solo se pinta cuando el grupo ya llegó.
  const codeVencido = isCodeExpired(group, transcurrido);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto space-y-6" data-testid="group-detail-notfound">
        <StickyBackPill onClick={() => navigate('/groups')} label={t('groups.back_btn')}
          testid="group-back-sticky" buttonTestid="group-back-btn" />
        <div className="glass-card rounded-[20px] p-8 text-center">
          <p className="text-sm text-[#a0a0a0]">{t('groups.not_found')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in-up" data-testid="group-detail-page">
      {/* LA SALIDA, PRIMERO Y SIEMPRE ALCANZABLE — y desde PILOTO-1D, TAMBIÉN
          ABAJO. Síntoma del smoke: en un grupo con lista larga el botón se iba
          de pantalla al recorrer a los alumnos y no había cómo volver sin subir
          hasta el tope. Es literalmente el mismo problema que COS-15 resolvió en
          el detalle de Analytics, así que se resuelve con LA MISMA píldora
          (components/StickyBackPill.js), no con una versión paralela. */}
      <StickyBackPill onClick={() => navigate('/groups')} label={t('groups.back_btn')}
        testid="group-back-sticky" buttonTestid="group-back-btn" />

      {errorDeCarga ? (
        <GroupsLoadError onRetry={load} testid="group-detail-error" />
      ) : group === null ? (
        <ListSkeleton rows={4} testid="skeleton-group-detail" />
      ) : (
        <>
          {/* Nombre + renombrar en el sitio.
              PIPE-INST T5.1 (firma #132, §4.1) — YA NO ES UNA TARJETA. El
              encabezado de una pantalla es su TÍTULO, y meterlo en un marco lo
              volvía un elemento más de una pila de tres marcos idénticos, sin
              jerarquía: el profesor no tenía forma de ver de un vistazo dónde
              empieza la pantalla. Fuera `glass-card` y su relleno; el contenido
              y el `data-testid` no se mueven. */}
          <div className="space-y-3" data-testid="group-header">
            {editing ? (
              <form onSubmit={saveName} className="flex flex-wrap items-center gap-2">
                <Input autoFocus value={draftName} onChange={e => setDraftName(e.target.value)}
                  maxLength={60}
                  className="flex-1 min-w-[12rem] bg-[#0e0e0e] border-[#262626] text-white rounded-xl min-h-[44px]"
                  data-testid="group-rename-input" />
                <Button type="submit" disabled={saving || !draftName.trim()}
                  className="bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl min-h-[44px] min-w-[44px] disabled:opacity-40"
                  data-testid="group-rename-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </Button>
                <Button type="button" variant="outline" onClick={() => setEditing(false)}
                  className="border-[#333] text-[#a0a0a0] rounded-xl min-h-[44px] min-w-[44px]"
                  data-testid="group-rename-cancel">
                  <X className="w-4 h-4" />
                </Button>
              </form>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  {/* t7-3 remate 8 (#591, C44 y C115): el nombre PARTE RENGLON y se lee
                      entero (a 375 se cortaba en "...grupo 3B"). Antes (PILOTO-1C) se
                      recortaba con puntos suspensivos para no empujar "Renombrar" fuera
                      de la primera pantalla; el boton sigue en su sitio porque la fila
                      ya se envuelve. */}
                  <h1 className="text-2xl font-bold text-white break-words" data-testid="group-name">{group.name}</h1>
                  <p className="text-sm text-[#6a6a6a] mt-1 flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" /> {conteoDeAlumnos(t, group.member_count)}
                  </p>
                </div>
                <Button variant="outline" onClick={startEdit}
                  className="border-[#333] text-[#a0a0a0] hover:text-white hover:border-[#555] rounded-xl min-h-[44px] text-sm"
                  data-testid="group-rename-btn">
                  <Pencil className="w-3.5 h-3.5 mr-2" /> {t('groups.rename_btn')}
                </Button>
              </div>
            )}

            {/* t7-2a (C1, #516): EL RECUADRO "AVANCE DEL GRUPO", arriba de todo y sin plegarse.
                Sus datos vienen en la MISMA respuesta de la pagina (`group.avance_resumen`).
                Debajo, la linea del correo del domingo (E3, #519). */}
            <AvanceDelGrupoRecuadro groupId={groupId} resumen={group.avance_resumen}
              reporteSemanal={group.reporte_semanal} />

            {/* Código y link, copiables — y con su reloj (PILOTO-1B).
                El código y el link MUEREN JUNTOS: son las dos mitades de la
                misma llave, así que la etiqueta de «Vencido» y el candado de los
                botones cubren a los dos. */}
            {/* PILOTO-1C — LOS DOS BOTONES, AL MISMO NIVEL (indicación de Carlos).
                Antes cada mitad era una columna con su propio botón al final, y
                como la URL ocupa dos o tres renglones y el código uno, el botón
                de «Copiar link» quedaba SIEMPRE más abajo que el de «Copiar
                código»: dos acciones gemelas a dos alturas distintas.
                Ahora los VALORES van arriba, en la rejilla de dos columnas, y
                los BOTONES en su propio renglón — así se alinean por
                construcción y no por suerte tipográfica. Y la URL se pinta en
                una sola línea con elipsis, igual que el código: nadie la lee
                carácter por carácter, se copia. */}
            {/* T5.1 (§4.1) — SIN EL MARCO, INTEGRADO COMO SECCIÓN DE TEXTO.
                Era una caja dentro de otra caja: dos bordes concéntricos para
                enseñar dos cadenas y tres botones. Se conserva TODO su contenido
                —código, link, sus dos botones al mismo nivel (PILOTO-1C), el
                reloj y «Regenerar»— y sólo se retira el marco. */}
            {/* T5.2 (#136 §4.2) — Y AHORA SE ASIENTA SOBRE SU PROPIA SUPERFICIE.
                T5.1 le quitó el marco con razón (era una caja dentro de otra),
                pero dejarlo suelto sobre el fondo lo volvió indistinguible de un
                párrafo más en una pila de texto — y es el dato que el profesor
                viene a copiar. La capa lo separa sin volver a enjaularlo: una
                superficie, no un marco concéntrico. */}
            {/* ── TANDA 33b (firmas #305 y #306) — EL BLOQUE DEL CÓDIGO SE PLIEGA ──
                PLEGADO, y siempre cerrado al entrar. El código de ingreso ocupa
                media pantalla —código grande, link, dos botones de copiar, la
                línea del reloj y «Generar código nuevo»— y es un dato que el
                profesor usa UNA VEZ, el día que reparte el grupo. Debajo vive el
                banco, que es donde pasa el tiempo: con el bloque siempre abierto
                el banco quedaba fuera de la primera pantalla en cada visita.

                ES EL MISMO CRITERIO QUE T5.3 (#141d) APLICÓ A LA LISTA DE
                ALUMNOS, y por eso el patrón se copia entero en vez de inventar
                otro. Aquel docblock lo dejó escrito y sigue valiendo: «una
                pantalla que se comporta distinto según por dónde entraste es una
                pantalla que el profesor no puede predecir.»

                Y NO ESCONDE NADA URGENTE: si el código está VENCIDO, su insignia
                sale en la cabecera cerrada. Es el único estado del bloque que
                pide acción, y se ve sin abrir.

                #306 — LA CABECERA ES LA TARJETA DEL CARRIL, la misma capa que los
                renglones de documento del banco (`CAPA`, la CONSTANTE y no una
                copia; desde R-3 de REMATES-350 (#347) es `CAPA_PLEGABLE`, la misma
                capa con el cuerpo un escalón más claro que el fondo).
                Antes era texto suelto con una flechita gris sobre el fondo
                de la página: nada decía que se pudiera tocar. El cuerpo abierto vive
                DENTRO de esta misma tarjeta, bajo una divisoria, para que abierto o
                cerrado sea un solo objeto.

                R-2 (PR #350, #342): la tarjeta YA NO lleva el vestido tocable
                (`CAPA_TOCABLE`): al pasar el puntero o al tocarla no cambia ni el
                fondo ni el borde. La banda se queda en su gris claro de reposo y el
                cuerpo en el oscuro de la capa, sin estado de puntero ni de presionado
                en ninguno de los dos; lo que avisa que se puede tocar es la flecha y
                el cursor de mano del botón. */}
            <div className={CAPA_DE_SECCION} data-testid="group-code-seccion">
              {/* t7-2a (C2, leccion 120): la capa de seccion del profe (la de la pagina de temas).
                  La cabecera entera abre y cierra; el chip VENCIDO se queda a la derecha,
                  antes de la flecha. El cuerpo no cambia. */}
              <h2 className={TITULO_DE_SECCION_PLEGABLE}>
              <button type="button"
                onClick={() => setCodigoAbierto(v => !v)}
                aria-expanded={codigoAbierto}
                className={CABECERA_DE_SECCION}
                data-testid="group-code-toggle">
                <span className={GUIA_DE_SECCION} aria-hidden="true" />
                {/* post-t7-1 (A7): a 390 el titulo se recortaba junto al sello VENCIDO; ahora parte renglon (dos renglones
                    caben en la cabecera de 56) */}
                <span className="flex-1 min-w-0 break-words" data-testid="group-code-titulo">
                  {t('groups.join_code_title')}
                </span>
                {codeVencido && (
                  <span className="shrink-0 text-[10px] uppercase tracking-wider text-amber-400 border border-amber-400/40 rounded px-1.5 py-0.5"
                    data-testid="group-code-expired-cabecera">
                    {t('groups.code_expired_badge')}
                  </span>
                )}
                <span aria-hidden="true"
                  className={`shrink-0 inline-flex text-[#8c8c8c]
                    transition-[transform] duration-200
                    ${codigoAbierto ? 'rotate-180' : ''}`}
                  data-testid="group-code-chevron">
                  <ChevronDown className="w-4 h-4" />
                </span>
              </button>
              </h2>
            {codigoAbierto && (
            <div className={CUERPO_DE_SECCION}
              data-testid="group-code-cuerpo">
              {/* TANDA 33b — SIN SU CAPA PROPIA. Hasta hoy este bloque traía la
                  suya (T5.2, #136 §4.2) porque flotaba suelto sobre el fondo y
                  había que separarlo del texto de al lado. Ahora vive DENTRO de
                  la tarjeta del plegable, así que su capa sería una segunda
                  concéntrica — exactamente lo que T5.1 (§4.1) quitó de esta
                  pantalla. El contenido no cambia ni un byte; sólo se va el
                  marco que ya pone el envoltorio. */}
              <div className="space-y-3" data-testid="group-detail-codigo-bloque">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="min-w-0 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className={ETIQUETA}>{t('groups.code_label')}</p>
                    {codeVencido && (
                      <span className="text-[10px] uppercase tracking-wider text-amber-400 border border-amber-400/40 rounded px-1.5 py-0.5"
                        data-testid="group-detail-code-expired">
                        {t('groups.code_expired_badge')}
                      </span>
                    )}
                  </div>
                  <p className={`font-mono text-2xl tracking-[0.25em] truncate ${codeVencido ? 'text-[#a0a0a0] line-through' : 'text-[#FF6B00]'}`}
                    data-testid="group-detail-code">
                    {group.join_code}
                  </p>
                </div>
                <div className="min-w-0 space-y-1.5">
                  <p className={ETIQUETA}>{t('groups.link_label')}</p>
                  {/* P-1 (PR #349, #325): el link va en el mismo naranja que el código
                      (y en gris tachado cuando venció, como el código). */}
                  <p className={`text-xs truncate font-mono ${codeVencido ? 'text-[#a0a0a0] line-through' : 'text-[#FF6B00]'}`}
                    data-testid="group-detail-link">
                    {joinLinkFor(group.join_code)}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={codeVencido}
                  onClick={() => copy(group.join_code, 'groups.toasts.code_copied')}
                  className="border-[#333] text-[#a0a0a0] hover:text-white hover:border-[#555] rounded-xl min-h-[44px] text-xs flex-1 disabled:opacity-40"
                  data-testid="group-detail-copy-code">
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> {t('groups.copy_code_btn')}
                </Button>
                <Button size="sm" variant="outline" disabled={codeVencido}
                  onClick={() => copy(joinLinkFor(group.join_code), 'groups.toasts.link_copied')}
                  className="border-[#333] text-[#a0a0a0] hover:text-white hover:border-[#555] rounded-xl min-h-[44px] text-xs flex-1 disabled:opacity-40"
                  data-testid="group-detail-copy-link">
                  <Link2 className="w-3.5 h-3.5 mr-1.5" /> {t('groups.copy_link_btn')}
                </Button>
              </div>
            </div>

            <p className="text-xs text-[#6a6a6a]" data-testid="group-detail-expiry">
              {codeVencido ? t('groups.code_expired_hint') : codeExpiryLabel(group, t, i18n?.language)}
            </p>

            {/* Vencido → ésta es la ÚNICA acción que queda, y se ve como tal. */}
            <Button variant={codeVencido ? 'default' : 'outline'}
              onClick={() => setConfirmRegen(true)}
              className={codeVencido
                ? 'w-full sm:w-auto bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl min-h-[44px] text-sm'
                : 'w-full sm:w-auto border-[#333] text-[#6a6a6a] hover:text-white hover:border-[#555] rounded-xl min-h-[44px] text-sm'}
              data-testid="group-detail-regenerate">
              <RefreshCw className="w-3.5 h-3.5 mr-2" /> {t('groups.regenerate_btn')}
            </Button>
            </div>
            )}
            </div>
            {/* PILOTO-1C — aquí vivía `groups.share_hint` («Comparte el código o
                el link. Tus alumnos no tienen que darte ningún dato nuevo…»).
                Fuera, y su clave i18n con él (indicación de Carlos): explicaba
                algo que los dos botones de arriba ya dicen solos, y era el único
                párrafo largo de una tarjeta que esta tanda compacta. */}
          </div>

          {/* La lista. PILOTO-1C — MÁS COMPACTA (indicación de Carlos): baja el
              padding y el aire de cada renglón; los 44 pt del botón «Quitar» no
              se tocan.

              PIPE-INST T5.1 (firma #132, §4.1) — Y AHORA SE PLIEGA. Fuera el
              marco de tarjeta, y el encabezado se vuelve el desplegador de su
              propia sección.

              ── T5.3 (firma #141d): ARRANCA PLEGADA, Y ES UN CAMBIO DE CRITERIO
              CON SU PORQUÉ ────────────────────────────────────────────────────

              T5.1 la dejó ABIERTA razonando que «quién está en este grupo» es lo
              que el profesor viene a mirar mientras reparte el código. El smoke
              enseñó la otra mitad, y pesa más: el BANCO —la puerta a todo el
              carril institucional, que es donde el profesor pasa el tiempo— vive
              debajo de esta sección, y con un salón de verdad queda fuera de la
              pantalla en cuanto se entra.

              PLEGARLA NO ESCONDE A LOS ALUMNOS: su número sigue en el
              encabezado, que es el dato de un vistazo; la lista queda a un
              toque, que es lo que cuesta cuando de verdad se necesita.

              SIN MEMORIA DE ESTADO Y SIN EXCEPCIÓN POR RUTA DE LLEGADA, también
              a propósito: una pantalla que se comporta distinto según por dónde
              entraste es una pantalla que el profesor no puede predecir. */}
          {/* TANDA 33b (firma #306) — Y AHORA SE ASIENTA SOBRE LA TARJETA DEL
              CARRIL, la misma de los renglones de documento del banco. Era texto
              suelto con una flechita gris sobre el fondo de la página: nada decía
              que la fila se pudiera tocar. `data-testid` nuevo para la tarjeta; el
              de la sección se conserva para no mover los candados que ya lo apuntan.

              R-2 (PR #350, #342): misma regla que el plegable del código: la
              tarjeta es sólo la capa, sin el vestido tocable; ni la banda ni el
              cuerpo cambian de color al pasar el puntero o al tocarlos. */}
          <div className={CAPA_DE_SECCION}
            data-testid="group-members-seccion">
          <div data-testid="group-members-section">
            {/* t7-2a (C2): la capa de seccion; titulo "Alumnos" y el conteo en su chip. */}
            <h2 className={TITULO_DE_SECCION_PLEGABLE}>
            <button type="button"
              onClick={() => setAlumnosAbiertos(v => !v)}
              aria-expanded={alumnosAbiertos}
              className={CABECERA_DE_SECCION}
              data-testid="group-members-toggle">
              <span className={GUIA_DE_SECCION} aria-hidden="true" />
              <span className="flex-1 min-w-0 truncate" data-testid="group-members-titulo">
                {t('groups.avance.seccion_alumnos')}
              </span>
              <span className={CHIP_DE_CONTEO} data-testid="group-members-conteo">
                {conteoDeAlumnos(t, group.member_count)}
              </span>
              <span aria-hidden="true"
                className={`shrink-0 inline-flex text-[#8c8c8c]
                  transition-[transform] duration-200
                  ${alumnosAbiertos ? 'rotate-180' : ''}`}
                data-testid="group-members-chevron">
                <ChevronDown className="w-4 h-4" />
              </span>
            </button>
            </h2>
            {!alumnosAbiertos ? null : (
            <div className={CUERPO_DE_SECCION}
              data-testid="group-members-cuerpo">
            {(group.members || []).length === 0 ? (
              <p className="text-sm text-[#555]" data-testid="group-members-empty">{t('groups.no_members')}</p>
            ) : (
              <div className="divide-y divide-[#1a1a1a]" data-testid="group-members-list">
                {group.members.map(m => (
                  <div key={m.user_id}
                    className="flex flex-wrap items-center justify-between gap-2 py-2"
                    data-testid={`group-member-${m.user_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{m.name || t('groups.member_no_name')}</p>
                      <p className="text-xs text-[#6a6a6a] truncate">{m.email}</p>
                      {/* PILOTO-1B — `joined_at` ya se guardaba desde PILOTO-1 y
                          ya viajaba en la respuesta: aquí SOLO se pinta. Cero
                          migración, cero campo nuevo. En la zona horaria del
                          navegador y en el idioma activo. */}
                      {joinedAtLabel(m.joined_at, t, i18n?.language) && (
                        <p className="text-[11px] text-[#555] truncate" data-testid={`group-member-joined-${m.user_id}`}>
                          {joinedAtLabel(m.joined_at, t, i18n?.language)}
                        </p>
                      )}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmRemove(m)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-xl min-h-[44px] text-xs"
                      data-testid={`group-remove-member-${m.user_id}`}>
                      <UserMinus className="w-3.5 h-3.5 mr-1.5" /> {t('groups.remove_member_btn')}
                    </Button>
                  </div>
                ))}
              </div>
            )}
            </div>
            )}
          </div>
          </div>

          {/* PILOTO-2 — EL BANCO, DEBAJO DE LA LISTA. El orden no es casual: la
              pantalla contesta primero «quien esta en este grupo» (que es lo que
              el profesor viene a ver mientras reparte el codigo) y despues «que
              material tiene». Invertirlo empujaria la lista de alumnos —la razon
              por la que esta pantalla existe desde PILOTO-1— fuera de la primera
              vista en un salon con material. */}
          <GroupBankSection groupId={groupId} />

          {/* PIPE-INST T5 (firma #126) — LA ZONA DESTRUCTIVA, AL FINAL DE TODO.
              Va DEBAJO del banco y no arriba con «Renombrar» a propósito: es la
              única acción de esta pantalla que destruye material pagado, y una
              acción así no comparte renglón con las que se tocan a diario. El
              profesor tiene que llegar hasta abajo para encontrarla, que es la
              fricción correcta — y la única, porque el modal ya dice todo lo
              demás. */}
          {/* t7-2a (C3): FUERA de toda seccion y de toda tarjeta, solo, a la derecha; rojo de
              contorno. La misma ventana de confirmacion de siempre. */}
          <div className="flex justify-end" data-testid="group-danger-zone">
            <button type="button" onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center justify-center h-11 hover:bg-red-400/10"
              style={{ borderRadius: 10, borderWidth: 1, borderStyle: 'solid', borderColor: '#e5484d', color: '#e5484d',
                fontWeight: 700, fontSize: 14, paddingLeft: 16, paddingRight: 16 }}
              data-testid="group-delete-btn">
              {t('groups.delete_group_btn')}
            </button>
          </div>
        </>
      )}

      <GroupDeleteModal
        abierto={confirmDelete}
        borrando={borrandoGrupo}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={borrarGrupo} />

      <RegenerateCodeModal
        open={confirmRegen}
        groupName={group?.name}
        expired={codeVencido}
        onCancel={() => setConfirmRegen(false)}
        onConfirm={regenerate}
        working={regenerating} />

      {/* Confirmación de quitar. `fixed` → escribe su propia safe area abajo. */}
      {confirmRemove && (
        <div className="fixed top-14 inset-x-0 bottom-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          data-testid="group-remove-modal">
          <div className="relative bg-[#111] border border-[#262626] rounded-[20px] p-6 sm:p-8 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-2">{t('groups.remove_title')}</h3>
            <p className="text-sm text-[#a0a0a0] mb-5">
              {t('groups.remove_body', { name: confirmRemove.name || confirmRemove.email })}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={() => setConfirmRemove(null)}
                className="flex-1 bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl min-h-[44px] text-sm"
                data-testid="group-remove-cancel">
                {t('common:cancel')}
              </Button>
              <Button variant="outline" onClick={removeMember} disabled={removing}
                className="flex-1 border-[#333] text-red-400 hover:text-red-300 rounded-xl min-h-[44px] text-sm"
                data-testid="group-remove-confirm">
                {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : t('groups.remove_confirm_btn')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
